/**
 * Builds a resolver that maps part names (and their aliases) to the canonical
 * option name from the options library. Use when importing templates or
 * matching presets so that legacy names (e.g. AH4909) are normalized to the
 * current name (e.g. FUS4909LT).
 */
export function buildPartNameResolver(
  options: ReadonlyArray<{ name?: string; aliases?: readonly string[] }>,
): (name: string) => string {
  const map = new Map<string, string>();
  for (const opt of options) {
    const canonical = typeof opt.name === "string" ? opt.name.trim() : "";
    if (!canonical) continue;
    map.set(canonical, canonical);
    if (Array.isArray(opt.aliases)) {
      for (const alias of opt.aliases) {
        if (typeof alias === "string" && alias.trim()) {
          map.set(alias.trim(), canonical);
        }
      }
    }
  }
  return (name: string): string => {
    if (typeof name !== "string" || !name.trim()) return name;
    return map.get(name.trim()) ?? name;
  };
}

/**
 * Resolves partSelection.name in a data object to canonical name when present.
 * Mutates data.partSelection.name in place when a mapping exists.
 */
export function resolvePartSelectionNameInData(
  data: Record<string, unknown>,
  resolvePartName: (name: string) => string,
): void {
  const partSelection = data?.partSelection;
  if (
    !partSelection ||
    typeof partSelection !== "object" ||
    Array.isArray(partSelection)
  ) {
    return;
  }
  const ps = partSelection as Record<string, unknown>;
  const name = ps.name;
  if (typeof name === "string" && name.trim()) {
    const canonical = resolvePartName(name);
    if (canonical !== name) {
      ps.name = canonical;
    }
  }
}

/** Fields that hold option-DB references; stored as { name } only. */
const OPTION_DB_REFERENCE_KEYS = [
  "partSelection",
  "rollerA",
  "rollerB",
  "areaPaddingType",
  "borderPaddingType",
] as const;

/** An article reference that named an id this environment does not have. */
export type UnresolvedPresetReference = { key: string; id: string };

/**
 * Turns the `{ id }` references a configurator's preset file carries into the
 * `{ name }` ones this app stores, using a map of the option database.
 *
 * The two halves of the system name an article differently on purpose: a
 * configurator reads by id, because an id survives a rename, while this app
 * matches by name, because that is what its aliases, its importer and its
 * own rename handling work on. A reference this map cannot resolve is left
 * as it is and handed back in `unresolved`: nothing further down the import
 * can turn an id into a name, so the caller has to decide, and the one
 * caller refuses the row rather than store a Vorlage that names an article
 * this environment does not have.
 */
export function resolvePresetReferenceIds(
  data: Record<string, unknown>,
  nameById: ReadonlyMap<string, string>,
): {
  data: Record<string, unknown>;
  unresolved: UnresolvedPresetReference[];
} {
  const result = { ...data };
  const unresolved: UnresolvedPresetReference[] = [];

  for (const key of OPTION_DB_REFERENCE_KEYS) {
    const value = result[key];
    if (!value || typeof value !== "object" || Array.isArray(value)) continue;

    const reference = value as { id?: unknown; name?: unknown };
    if (typeof reference.name === "string" && reference.name.length > 0) {
      continue;
    }
    const id =
      typeof reference.id === "string" ? reference.id.toLowerCase() : "";
    const name = id ? nameById.get(id) : undefined;
    if (name) {
      result[key] = { ...reference, name };
    } else if (id) {
      unresolved.push({ key, id });
    }
  }

  return { data: result, unresolved };
}

/**
 * Strips embedded option-DB copies from preset data down to name-only
 * references.  Templates never store full copies – only `{ name }`.
 * Full objects are resolved from the OptionDB at load time.
 */
export function stripPresetReferences(
  data: Record<string, unknown>,
): Record<string, unknown> {
  const result = { ...data };

  for (const key of OPTION_DB_REFERENCE_KEYS) {
    const val = result[key];
    if (val && typeof val === "object" && !Array.isArray(val)) {
      const name = (val as { name?: string }).name;
      if (name) {
        result[key] = { name };
      }
    }
  }

  return result;
}
